/**
 * chatService — Singleton WebSocket client for the consumer web app (PEL-836).
 *
 * Mirrors the mobile `ChatService` surface exactly so hooks and screens can be
 * written identically across `apps/pelipost-mobile` and `apps/web`. Connects
 * to the backend Socket.IO gateway on the `/support` namespace with the user's
 * JWT access token.
 *
 * Why this is a singleton (not a React context): the socket lifecycle is tied
 * to authentication, not to any particular React tree. AuthContext calls
 * `chatService.connect(...)` on login and `chatService.disconnect()` on logout.
 * Hooks subscribe via `chatService.on('event', handler)` with cleanup in
 * `useEffect` return values.
 *
 * Usage:
 *   import { chatService } from '@/lib/chatService';
 *   chatService.connect(apiBaseUrl, jwtToken);
 *   chatService.on('unread_count', ({ count }) => { ... });
 *   chatService.sendMessage(conversationId, body);
 */

import EventEmitter from 'eventemitter3';
import { io, type Socket } from 'socket.io-client';

// ────────────────────────────────────────────────────────────────
// Types (kept in sync with apps/pelipost-mobile/src/services/ChatService.ts)
// ────────────────────────────────────────────────────────────────

export interface SupportMessage {
  id: string;
  conversationId: string;
  senderType: 'customer' | 'agent' | 'system';
  senderId: string;
  body: string;
  attachments: Record<string, unknown>[] | null;
  isInternalNote: boolean;
  readAt: string | null;
  readBy: string | null;
  createdAt: string;
}

export interface UnreadCountPayload {
  count: number;
}

export interface MessageReadPayload {
  conversationId: string;
  messageId: string;
  readBy: string;
  readAt: string;
}

export interface UserTypingPayload {
  conversationId: string;
  userId: string;
  userName: string;
  isTyping: boolean;
}

export interface WsErrorPayload {
  event: string;
  message: string;
}

export interface ChatEvents {
  unread_count: (data: UnreadCountPayload) => void;
  new_message: (data: SupportMessage) => void;
  message_read: (data: MessageReadPayload) => void;
  user_typing: (data: UserTypingPayload) => void;
  ws_error: (data: WsErrorPayload) => void;
  connected: () => void;
  disconnected: (reason: string) => void;
}

// ────────────────────────────────────────────────────────────────
// Service
// ────────────────────────────────────────────────────────────────

class ChatService extends EventEmitter<ChatEvents> {
  private socket: Socket | null = null;

  /**
   * Connect to the WebSocket gateway.
   *
   * @param apiBaseUrl  Full API URL, e.g. "https://dev-api.pelipost.com/api/v1/mobile".
   *                    We strip everything after the host to get the Socket.IO origin.
   * @param jwtToken    Same JWT used for REST API calls.
   */
  connect(apiBaseUrl: string, jwtToken: string) {
    if (this.socket?.connected) return;

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Extract the origin host from the REST base URL:
    //   "https://dev-api.pelipost.com/api/v1"        → "https://dev-api.pelipost.com"
    //   "https://dev-api.pelipost.com/api/v1/mobile" → "https://dev-api.pelipost.com"
    let wsUrl: string;
    try {
      const parsed = new URL(apiBaseUrl);
      wsUrl = `${parsed.protocol}//${parsed.host}`;
    } catch {
      wsUrl = apiBaseUrl.replace(/\/api\/.*$/, '');
    }

    // Browsers use long-polling fallback automatically. Unlike mobile we do NOT
    // force `transports: ['websocket']` — some corporate proxies block raw WS.
    this.socket = io(`${wsUrl}/support`, {
      auth: { token: jwtToken },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.socket.on('connect', () => {
      this.emit('connected');
    });

    this.socket.on('disconnect', (reason: string) => {
      this.emit('disconnected', reason);
    });

    this.socket.on('connect_error', (err: Error) => {
      // eslint-disable-next-line no-console
      console.warn('[chatService] Connection error:', err.message);
    });

    this.socket.on('unread_count', (data: UnreadCountPayload) => {
      this.emit('unread_count', data);
    });

    this.socket.on('new_message', (data: SupportMessage) => {
      this.emit('new_message', data);
    });

    this.socket.on('message_read', (data: MessageReadPayload) => {
      this.emit('message_read', data);
    });

    this.socket.on('user_typing', (data: UserTypingPayload) => {
      this.emit('user_typing', data);
    });

    this.socket.on('error', (data: WsErrorPayload) => {
      this.emit('ws_error', data);
    });
  }

  /** Disconnect and clean up. Call on logout. */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /** Join a conversation room so the server starts emitting new_message for it. */
  joinConversation(conversationId: string) {
    this.socket?.emit('join_conversation', { conversationId });
  }

  /** Leave a conversation room when the user navigates away. */
  leaveConversation(conversationId: string) {
    this.socket?.emit('leave_conversation', { conversationId });
  }

  /**
   * Send a message via WebSocket. Uses Socket.IO acknowledgment callback to
   * confirm persistence; rejects if not connected or if the server returns
   * { error }.
   */
  sendMessage(
    conversationId: string,
    body: string,
    attachments?: object[],
  ): Promise<{ status: string; messageId: string }> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Not connected'));
        return;
      }
      this.socket.emit(
        'send_message',
        { conversationId, body, attachments },
        (response: { status: string; messageId: string } | { error: string }) => {
          if ('error' in response) reject(new Error(response.error));
          else resolve(response);
        },
      );
    });
  }

  sendTypingStart(conversationId: string) {
    this.socket?.emit('typing_start', { conversationId });
  }

  sendTypingStop(conversationId: string) {
    this.socket?.emit('typing_stop', { conversationId });
  }

  markRead(conversationId: string, messageId: string) {
    this.socket?.emit('mark_read', { conversationId, messageId });
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

/** Singleton instance — import this throughout the app. */
export const chatService = new ChatService();

// Expose the singleton on `window` in dev builds for Playwright capture +
// manual debugging. This is a no-op in production bundles because `import.meta.env.DEV`
// folds to `false` at build time and the branch gets tree-shaken.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __pelipostChatService?: ChatService }).__pelipostChatService =
    chatService;
}
